/* Supply › Reports › Purchase › Supplier-wise Purchase
 * Purchase totals grouped by supplier over a date range. */
import React, { useEffect, useMemo, useState } from "react";
import supplyService from "../../../Services/supplyService";
import { useWarehouse } from "../../Layout/WarehouseContext";
import { cbFmtINR, cbFmtNum } from "../supplyData";
import {
  Button,
  Table,
  Tr,
  Td,
  SectionHeader,
  ToolbarBar,
  DateRangeFilter,
  KpiBox,
  LoadingState,
  ErrorState,
  IconDownload,
} from "../supplyKit";
import { exportToXlsx } from "./reportUtils";
import { ReportBack } from "./reportShared";
import { PHARMACY_ROUTES } from "../../../Route/path";

const PurchaseBySupplier = () => {
  /* The store in scope, chosen once in the topbar. */
  const { code: wh } = useWarehouse();
  const [suppliers, setSuppliers] = useState([]);
  const [grns, setGrns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [range, setRange] = useState({ fromDate: "", toDate: "" });

  useEffect(() => {
    let alive = true;
    supplyService
      .getSuppliers()
      .then((s) => alive && setSuppliers(s || []))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [reloadKey]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    const params = { sort: "invoiceDate,desc", size: 500 };
    /* Always scoped to the store selected in the topbar — for a head too, who now
       switches store there rather than reporting across all of them at once. */
    if (wh) params.warehouseId = wh;
    if (range.fromDate) params.fromDate = range.fromDate;
    if (range.toDate) params.toDate = range.toDate;
    supplyService
      .getGrns(params)
      .then((res) => {
        if (!alive) return;
        setGrns(res.content || []);
        setLoading(false);
      })
      .catch((e) => {
        if (!alive) return;
        setError(e?.message || "Could not load supplier-wise purchase.");
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [reloadKey, wh, range.fromDate, range.toDate]);

  const supName = (id, fallback) => suppliers.find((s) => s.id === id)?.name || fallback || `Supplier ${id}`;

  const groups = useMemo(() => {
    const map = {};
    grns.forEach((g) => {
      const key = g.supplier ?? g.supplierName;
      if (!map[key]) map[key] = { key, name: supName(g.supplier, g.supplierName), grns: 0, units: 0, value: 0 };
      map[key].grns += 1;
      map[key].units += Number(g.units) || 0;
      map[key].value += Number(g.value) || 0;
    });
    return Object.values(map).sort((a, b) => b.value - a.value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grns, suppliers]);

  const totals = groups.reduce(
    (acc, g) => {
      acc.grns += g.grns;
      acc.units += g.units;
      acc.value += g.value;
      return acc;
    },
    { grns: 0, units: 0, value: 0 }
  );

  const handleExport = () => {
    const headers = ["Supplier", "GRNs", "Units", "Purchase value"];
    const rows = groups.map((g) => [g.name, g.grns, g.units, Number(g.value.toFixed(2))]);
    exportToXlsx("purchase-by-supplier.xlsx", headers, rows, "By supplier");
  };

  return (
    <div className="cb-fade-in" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <ReportBack to={PHARMACY_ROUTES.PURCHASE_REPORT} label="Purchase Reports" />
      <SectionHeader
        title="Supplier-wise Purchase"
        sub="Purchase totals grouped by supplier over a date range"
        actions={
          <Button icon={<IconDownload size={14} />} onClick={handleExport} disabled={!groups.length}>
            Export
          </Button>
        }
      />

      <ToolbarBar>
        <DateRangeFilter
          from={range.fromDate}
          to={range.toDate}
          onFromChange={(v) => setRange((r) => ({ ...r, fromDate: v }))}
          onToChange={(v) => setRange((r) => ({ ...r, toDate: v }))}
        />
        <Button onClick={() => setRange({ fromDate: "", toDate: "" })}>Reset</Button>
        <div style={{ marginLeft: "auto", fontSize: 11, color: "var(--fg-muted)", fontFamily: "var(--font-mono)" }}>
          {cbFmtNum(groups.length)} suppliers
        </div>
      </ToolbarBar>

      <div className="grid grid-cols-1 sm:grid-cols-3" style={{ display: "grid", gap: 10 }}>
        <KpiBox label="Suppliers" value={cbFmtNum(groups.length)} variant="ink" />
        <KpiBox label="GRNs" value={cbFmtNum(totals.grns)} variant="ink" />
        <KpiBox label="Purchase value" value={cbFmtINR(totals.value)} variant="green" />
      </div>

      {loading ? (
        <LoadingState label="Loading supplier-wise purchase…" />
      ) : error ? (
        <ErrorState message={error} onRetry={() => setReloadKey((k) => k + 1)} />
      ) : (
        <Table
          columns={[
            { label: "Supplier" },
            { label: "GRNs", align: "right" },
            { label: "Units", align: "right" },
            { label: "Purchase value", align: "right" },
            { label: "Share", align: "right" },
          ]}
        >
          {groups.length === 0 && (
            <tr>
              <Td colSpan={5} style={{ textAlign: "center", color: "var(--fg-muted)", height: 80 }}>
                No purchases in this range.
              </Td>
            </tr>
          )}
          {groups.map((g) => {
            const share = totals.value ? Math.round((g.value / totals.value) * 100) : 0;
            return (
              <Tr key={g.key}>
                <Td style={{ fontWeight: 600, color: "var(--fg-strong)" }}>{g.name}</Td>
                <Td mono right>
                  {cbFmtNum(g.grns)}
                </Td>
                <Td mono right>
                  {cbFmtNum(g.units)}
                </Td>
                <Td mono right style={{ fontWeight: 600 }}>
                  {cbFmtINR(g.value)}
                </Td>
                <Td mono right>
                  {share}%
                </Td>
              </Tr>
            );
          })}
        </Table>
      )}
    </div>
  );
};

export default PurchaseBySupplier;
